import React, { useState, useCallback } from 'react';
import { Container, Modal } from 'react-bootstrap';
import Breadcrumbs from '../../Common/Breadcrumbs';
import CommunitySection from './components/CommunitySection';
import AddNewCommunityForm from './components/AddNewCommunityForm';
import { DeleteModal } from '../../Common/DeleteModal';
import { CommunityVideo, VideoCategory } from '../../api/community/types';
import { communityService } from '../../api/community/communityService';
import { toast } from 'react-toastify';
import './CommunityListPage.css';

const CommunityListPage: React.FC = () => {
  const [selectedVideo, setSelectedVideo] = useState<CommunityVideo | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormCategory, setAddFormCategory] = useState<VideoCategory>(VideoCategory.SOCIAL);
  const [editingVideo, setEditingVideo] = useState<CommunityVideo | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<CommunityVideo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleViewVideo = (video: CommunityVideo) => {
    setSelectedVideo(video);
    // Navigate to video details page or open modal
    console.log('View video:', video);
    // TODO: Implement navigation or modal
  };

  const handleEditVideo = (video: CommunityVideo) => {
    setEditingVideo(video);
    // Map video category to VideoCategory enum
    if (video.category === 'action') {
      setAddFormCategory(VideoCategory.ACTION);
    } else if (video.category === 'level_up') {
      setAddFormCategory(VideoCategory.LEVEL_UP);
    } else {
      setAddFormCategory(VideoCategory.SOCIAL);
    }
    setShowAddForm(true);
  };

  const handlePublishVideo = async (video: CommunityVideo) => {
    // Check if it's a Level Up series
    const isLevelUpSeries = video.category === 'level_up' && 
      (video.contentType === 'series' || video.videoType === 'series');
    
    // For Level Up series, validate that it has chapters before publishing
    if (isLevelUpSeries) {
      const totalChapters = (video as any).totalChapters || 0;
      if (totalChapters === 0) {
        toast.error('Cannot publish series without chapters. Please add at least one chapter before publishing.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        return;
      }
    }
    
    try {
      // For Level Up series, use series publish API, otherwise use video publish API
      if (isLevelUpSeries) {
        await communityService.publishSeries(video._id);
        toast.success('Series published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.publishVideo(video._id);
        toast.success('Video published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      // Refresh all sections
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error publishing:', error);
      
      let errorMessage = video.category === 'level_up' && video.videoType === 'series' 
        ? 'Failed to publish series. Please try again.' 
        : 'Failed to publish video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const handleUnpublishVideo = async (video: CommunityVideo) => {
    try {
      // For Level Up series, use archive API, otherwise use video reject API
      if (video.category === 'level_up' && video.videoType === 'series') {
        await communityService.archiveSeries(video._id);
        toast.success('Series archived successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.rejectVideo(video._id);
        toast.success('Video unpublished successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      // Refresh all sections
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error unpublishing:', error);
      
      let errorMessage = video.category === 'level_up' && video.videoType === 'series' 
        ? 'Failed to archive series. Please try again.' 
        : 'Failed to unpublish video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const handleDeleteVideo = (video: CommunityVideo) => {
    setVideoToDelete(video);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setVideoToDelete(null);
    setDeleteError(null);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;

    // Check if it's a Level Up series
    const isLevelUpSeries = videoToDelete.category === 'level_up' && videoToDelete.videoType === 'series';

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      if (isLevelUpSeries) {
        await communityService.deleteSeries(videoToDelete._id);
        toast.success('Series deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.deleteVideo(videoToDelete._id);
        toast.success('Video deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      // Close modal
      handleCloseDeleteModal();
      
      // Refresh all sections
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error(`Error deleting ${isLevelUpSeries ? 'series' : 'video'}:`, error);
      
      // Extract error message
      let errorMessage = isLevelUpSeries 
        ? 'Failed to delete series. Please try again.'
        : 'Failed to delete video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setDeleteError(errorMessage);
      
      // Show error toast
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNew = (category: VideoCategory) => {
    setAddFormCategory(category);
    setShowAddForm(true);
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setEditingVideo(null);
  };

  const handleFormSuccess = useCallback(() => {
    // For Level Up series/chapters, the form stays open to add more chapters
    // Only close the form if it's not Level Up
    // Note: For Level Up, the form will only close when:
    // 1. User clicks Cancel/Close button
    // 2. Batch chapter upload completes (which calls onHide() explicitly)
    if (addFormCategory !== VideoCategory.LEVEL_UP) {
      setShowAddForm(false);
      setEditingVideo(null);
    }
    
    // Trigger refresh immediately without delay for faster UI update
    // The API call includes cache-busting parameter (_t) to ensure fresh data
    setRefreshKey(prev => prev + 1);
  }, [addFormCategory]);

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Community', active: true }
            ]} 
          />
        </div>

        {/* Community Sections */}
        <div className="community-sections-container" style={{ padding: '0 15px' }}>
          <CommunitySection
            category={VideoCategory.ACTION}
            categoryLabel="Action"
            onEditVideo={handleEditVideo}
            onUnpublishVideo={handleUnpublishVideo}
            onDeleteVideo={handleDeleteVideo}
            onAddNew={handleAddNew}
            refreshKey={refreshKey}
          />

          <CommunitySection
            category={VideoCategory.LEVEL_UP}
            categoryLabel="Level Up"
            onEditVideo={handleEditVideo}
            onPublishVideo={handlePublishVideo}
            onUnpublishVideo={handleUnpublishVideo}
            onDeleteVideo={handleDeleteVideo}
            onAddNew={handleAddNew}
            refreshKey={refreshKey}
          />

          <CommunitySection
            category={VideoCategory.SOCIAL}
            categoryLabel="Social"
            onEditVideo={handleEditVideo}
            onUnpublishVideo={handleUnpublishVideo}
            onDeleteVideo={handleDeleteVideo}
            onAddNew={handleAddNew}
            refreshKey={refreshKey}
          />
        </div>
      </Container>

      {/* Add New Form Panel */}
      <AddNewCommunityForm
        show={showAddForm}
        onHide={handleCloseAddForm}
        category={addFormCategory}
        onSuccess={handleFormSuccess}
        video={editingVideo || undefined}
        mode={editingVideo ? 'edit' : 'create'}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deleteModalFunction={confirmDeleteVideo}
        title={(videoToDelete?.category === 'level_up' && videoToDelete?.videoType === 'series') ? 'Delete Series' : 'Delete Video'}
        message={videoToDelete 
          ? `Are you sure you want to delete "${videoToDelete.title}"? This action cannot be undone.` 
          : 'Are you sure you want to delete this item?'}
        confirmLabel={(videoToDelete?.category === 'level_up' && videoToDelete?.videoType === 'series') ? 'Delete Series' : 'Delete Video'}
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
};

export default CommunityListPage;
